import { openDB } from 'idb';
import { DATA_VERSION } from './version';

// Banco compartilhado criado pelo defeito de cache entre contas: quando o
// módulo carregava fora de uma URL de conta, o accountId era '' e o nome do
// banco virava `cw-store-`, um só para todas as contas. Ele é 100% cache
// (inbox, label, team e as chaves de validação), reconstruído da rede na
// primeira leitura, então apagar não perde dado nenhum.
export const SHARED_CACHE_DB_NAME = 'cw-store-';

// Remove o banco sujo do navegador de quem já usou a versão com defeito, sem
// pedir nada ao usuário. Idempotente: apagar banco inexistente não é erro.
export const deleteSharedCacheDb = async () => {
  try {
    const indexedDb = window?.indexedDB;
    if (!indexedDb) return;

    indexedDb.deleteDatabase(SHARED_CACHE_DB_NAME);

    const dbNames = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
    const restantes = dbNames.filter(name => name !== SHARED_CACHE_DB_NAME);
    if (restantes.length !== dbNames.length) {
      localStorage.setItem('cw-idb-names', JSON.stringify(restantes));
    }
  } catch {
    // Navegador sem IndexedDB ou com storage bloqueado: nada a limpar.
  }
};

export class DataManager {
  constructor(accountId) {
    this.modelsToSync = ['inbox', 'label', 'team'];
    this.accountId = accountId;
    this.db = null;
  }

  async initDb() {
    if (this.db) return this.db;
    const dbName = `cw-store-${this.accountId}`;
    this.db = await openDB(`cw-store-${this.accountId}`, DATA_VERSION, {
      upgrade(db) {
        db.createObjectStore('cache-keys');
        db.createObjectStore('inbox', { keyPath: 'id' });
        db.createObjectStore('label', { keyPath: 'id' });
        db.createObjectStore('team', { keyPath: 'id' });
      },
    });

    // Store the database name in LocalStorage
    const dbNames = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
    if (!dbNames.includes(dbName)) {
      dbNames.push(dbName);
      localStorage.setItem('cw-idb-names', JSON.stringify(dbNames));
    }

    return this.db;
  }

  validateModel(name) {
    if (!name) throw new Error('Model name is not defined');
    if (!this.modelsToSync.includes(name)) {
      throw new Error(`Model ${name} is not defined`);
    }
    return true;
  }

  async replace({ modelName, data }) {
    this.validateModel(modelName);

    this.db.clear(modelName);
    return this.push({ modelName, data });
  }

  async push({ modelName, data }) {
    this.validateModel(modelName);

    if (Array.isArray(data)) {
      const tx = this.db.transaction(modelName, 'readwrite');
      data.forEach(item => {
        tx.store.add(item);
      });
      await tx.done;
    } else {
      await this.db.add(modelName, data);
    }
  }

  async get({ modelName }) {
    this.validateModel(modelName);
    return this.db.getAll(modelName);
  }

  async setCacheKeys(cacheKeys) {
    Object.keys(cacheKeys).forEach(async modelName => {
      this.db.put('cache-keys', cacheKeys[modelName], modelName);
    });
  }

  async getCacheKey(modelName) {
    this.validateModel(modelName);

    return this.db.get('cache-keys', modelName);
  }
}
